const scriptUrl = 'https://accounts.google.com/gsi/client'

export const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? ''
export const isGoogleIdentityConfigured = Boolean(googleClientId)

interface GoogleCredentialResponse {
  credential?: string
}

interface GoogleButtonOptions {
  type: 'standard'
  theme: 'filled_black'
  size: 'large'
  text: 'continue_with'
  shape: 'pill'
  logo_alignment: 'center'
  width: number
  locale: string
}

interface GoogleAccountsId {
  initialize(options: {
    client_id: string
    callback: (response: GoogleCredentialResponse) => void
    nonce: string
    ux_mode: 'popup'
    auto_select: boolean
    use_fedcm_for_button: boolean
  }): void
  renderButton(parent: HTMLElement, options: GoogleButtonOptions): void
  disableAutoSelect(): void
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } }
  }
}

let loading: Promise<GoogleAccountsId> | null = null

export function loadGoogleIdentity(): Promise<GoogleAccountsId> {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id)
  if (loading) return loading
  loading = new Promise<GoogleAccountsId>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = scriptUrl
    script.async = true
    script.defer = true
    script.onload = () => {
      const accounts = window.google?.accounts?.id
      if (accounts) resolve(accounts)
      else reject(new Error('Google Identity Services did not initialize.'))
    }
    script.onerror = () => reject(new Error('Google Identity Services could not be loaded.'))
    document.head.append(script)
  }).catch((error: unknown) => {
    loading = null
    document.querySelectorAll(`script[src="${scriptUrl}"]`).forEach((node) => node.remove())
    throw error
  })
  return loading
}

function toHex(bytes: ArrayBuffer | Uint8Array): string {
  return [...new Uint8Array(bytes)].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

export async function createNonce(): Promise<{ raw: string; hashed: string }> {
  const raw = toHex(crypto.getRandomValues(new Uint8Array(32)))
  const hashed = toHex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw)))
  return { raw, hashed }
}

export async function renderGoogleButton(
  parent: HTMLElement,
  width: number,
  onCredential: (credential: string, nonce: string) => void,
): Promise<void> {
  const [accounts, nonce] = await Promise.all([loadGoogleIdentity(), createNonce()])
  accounts.initialize({
    client_id: googleClientId,
    callback: (response) => {
      if (response.credential) onCredential(response.credential, nonce.raw)
    },
    nonce: nonce.hashed,
    ux_mode: 'popup',
    auto_select: false,
    use_fedcm_for_button: true,
  })
  parent.replaceChildren()
  accounts.renderButton(parent, {
    type: 'standard',
    theme: 'filled_black',
    size: 'large',
    text: 'continue_with',
    shape: 'pill',
    logo_alignment: 'center',
    width: Math.round(Math.min(400, Math.max(200, width))),
    locale: 'en',
  })
}

export function disableGoogleAutoSelect(): void {
  window.google?.accounts?.id?.disableAutoSelect()
}
